import React, { useEffect, useState } from 'react';
import { User, CheckCircle } from 'lucide-react';
import { isVerifiedAction } from '../utils/tiers';
import AppLogo from './AppLogo';
import { getCommunities } from '../services/communitiesService';
import { fetchProfileCommunityFields, updateProfileCountryCommunity } from '../services/profilesService';
import { ONBOARDING_COUNTRIES, ONBOARDING_UI } from '../config/onboardingConfig';

const ProfileView = ({ user, userActions, userRewards, tierStats, onLogout, onProfileCommunityUpdate }) => {
  const [country, setCountry] = useState('');
  const [communityId, setCommunityId] = useState('');
  const [communities, setCommunities] = useState([]);
  const [profileLoading, setProfileLoading] = useState(true);
  const [communitiesLoading, setCommunitiesLoading] = useState(false);
  const [communitiesError, setCommunitiesError] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [saveError, setSaveError] = useState('');

  useEffect(() => {
    if (!user?.id) return;

    let cancelled = false;
    (async () => {
      setProfileLoading(true);
      setSaveMessage('');
      setSaveError('');
      try {
        const { country: c, community_id: cid } = await fetchProfileCommunityFields(user.id);
        if (!cancelled) {
          setCountry(c || '');
          setCommunityId(cid || '');
        }
      } catch (e) {
        if (!cancelled) {
          setCountry(user.country || '');
          setCommunityId(user.communityId || '');
          setSaveError(e?.message || 'Could not refresh profile fields');
        }
      } finally {
        if (!cancelled) setProfileLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load from Supabase on user id only; omit user.country/communityId so App state updates after save do not reset the form
  }, [user?.id]);

  useEffect(() => {
    if (!country) {
      setCommunities([]);
      setCommunityId('');
      setCommunitiesError('');
      return;
    }

    let cancelled = false;
    setCommunitiesLoading(true);
    setCommunitiesError('');

    getCommunities(country)
      .then((rows) => {
        if (cancelled) return;
        setCommunities(rows);
        setCommunityId((prev) => (prev && rows.some((r) => r.id === prev) ? prev : ''));
      })
      .catch((e) => {
        if (!cancelled) {
          setCommunities([]);
          setCommunitiesError(e?.message || 'Could not load communities');
        }
      })
      .finally(() => {
        if (!cancelled) setCommunitiesLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [country]);

  const selectedCommunityName = communities.find((c) => c.id === communityId)?.name;

  const handleSaveCommunity = async () => {
    if (!user?.id || !country || !communityId) {
      setSaveError('Please select a country and a community.');
      return;
    }
    setSaving(true);
    setSaveMessage('');
    setSaveError('');
    try {
      await updateProfileCountryCommunity(user.id, {
        country,
        community_id: communityId,
      });
      if (onProfileCommunityUpdate) {
        onProfileCommunityUpdate({
          country,
          communityId,
          communityLabel: selectedCommunityName || null,
        });
      }
      setSaveMessage('Your community has been saved.');
    } catch (e) {
      setSaveError(e?.message || 'Could not save changes');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold mb-2">Profile</h2>
        <p className="text-gray-600">Your climate action journey</p>
      </div>

      <div className="bg-white rounded-xl p-6 shadow-sm border">
        <div className="flex items-center gap-4 mb-6">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
            <User className="h-8 w-8 text-green-600" />
          </div>
          <div>
            <h3 className="text-xl font-semibold">{user.name}</h3>
            <p className="text-gray-600">{user.email}</p>
            {user.joinDate && (
              <p className="text-sm text-gray-500">
                Member since {new Date(user.joinDate).toLocaleDateString()}
              </p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 text-center">
          <div className="p-4 bg-amber-50 rounded-lg border border-amber-100">
            <div className="text-xl font-bold text-amber-900">{tierStats?.tierLabel ?? 'No tier'}</div>
            <div className="text-sm text-gray-600">Tier (last 7 days)</div>
          </div>
          <div className="p-4 bg-green-50 rounded-lg">
            <div className="text-2xl font-bold text-green-600">{tierStats?.verifiedCountLast7Days ?? 0}</div>
            <div className="text-sm text-gray-600">Verified actions (last 7 days)</div>
          </div>
          <div className="p-4 bg-blue-50 rounded-lg">
            <div className="text-2xl font-bold text-blue-600">{userActions.length}</div>
            <div className="text-sm text-gray-600">Total actions</div>
          </div>
          <div className="p-4 bg-orange-50 rounded-lg">
            <div className="text-2xl font-bold text-orange-600">{userRewards.length}</div>
            <div className="text-sm text-gray-600">Total rewards given</div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl p-6 shadow-sm border">
        <h3 className="text-lg font-semibold mb-1">Your Community</h3>
        <p className="text-sm text-gray-600 mb-4">
          This is your campus or local group — separate from the Community feed of news and updates.
        </p>

        {profileLoading ? (
          <p className="text-sm text-gray-500">Loading…</p>
        ) : (
          <>
            {selectedCommunityName ? (
              <p className="text-sm font-medium text-gray-800 mb-4">
                Your community: <span className="text-green-800">{selectedCommunityName}</span>
                {country ? (
                  <span className="text-gray-600 font-normal"> · {country}</span>
                ) : null}
              </p>
            ) : country ? (
              <p className="text-sm text-gray-600 mb-4">
                Country: {country}
                {communityId ? null : ' — choose a community below.'}
              </p>
            ) : null}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                <select
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm"
                >
                  <option value="">Select your country</option>
                  {ONBOARDING_COUNTRIES.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Community</label>
                {!country ? (
                  <p className="text-sm text-gray-500">Select a country first.</p>
                ) : communitiesLoading ? (
                  <p className="text-sm text-gray-500">Loading communities…</p>
                ) : communitiesError ? (
                  <p className="text-sm text-red-600">{communitiesError}</p>
                ) : communities.length === 0 ? (
                  <p className="text-sm text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                    {ONBOARDING_UI.noCommunitiesForCountry}
                  </p>
                ) : (
                  <select
                    value={communityId}
                    onChange={(e) => setCommunityId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm"
                  >
                    <option value="">Select your community</option>
                    {communities.map((com) => (
                      <option key={com.id} value={com.id}>
                        {com.name}
                        {com.communityType ? ` (${com.communityType})` : ''}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              <button
                type="button"
                onClick={handleSaveCommunity}
                disabled={saving || !country || !communityId || !!communitiesError || communities.length === 0}
                className="px-4 py-2 bg-green-600 text-white rounded-lg font-medium text-sm hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                {saving ? 'Saving…' : 'Save'}
              </button>

              {saveMessage ? <p className="text-sm text-green-700">{saveMessage}</p> : null}
              {saveError ? <p className="text-sm text-red-600">{saveError}</p> : null}
            </div>
          </>
        )}
      </div>

      <div className="bg-white rounded-xl p-6 shadow-sm border">
        <h3 className="text-lg font-semibold mb-4">All climate actions</h3>
        {userActions.length === 0 ? (
          <div className="text-center py-8">
            <AppLogo className="h-12 w-12 mx-auto mb-2 object-contain opacity-40" />
            <p className="text-gray-600">No actions logged yet</p>
          </div>
        ) : (
          <div className="space-y-3">
            {userActions.map((action) => (
              <div key={action.id} className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
                <div className="p-2 bg-green-100 rounded-lg">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium">{action.title}</div>
                  <div className="text-sm text-gray-600">
                    {new Date(action.created_at).toLocaleDateString()} at{' '}
                    {new Date(action.created_at).toLocaleTimeString()}
                  </div>
                  {action.description && (
                    <p className="text-sm text-gray-700 mt-1 whitespace-pre-wrap break-words">{action.description}</p>
                  )}
                  <div className="mt-2">
                    <span
                      className={`text-xs font-medium px-2 py-0.5 rounded ${
                        isVerifiedAction(action) ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-900'
                      }`}
                    >
                      {isVerifiedAction(action) ? 'Verified' : 'Pending verification'}
                    </span>
                  </div>
                </div>
                {(action.photo_url || action.photo) && (
                  <img
                    src={action.photo_url || action.photo}
                    alt="Action proof"
                    className="w-12 h-12 rounded-lg object-cover shrink-0"
                  />
                )}
                <div className="text-xs text-gray-500 px-2 py-1 bg-white rounded shrink-0">
                  {action.method === 'photo' ? '📷 Photo' : action.method === 'text' ? '📝 Text' : '✏️ Manual'}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-white rounded-xl p-6 shadow-sm border">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-sem-semibold">Account settings</h3>
          <button
            type="button"
            onClick={onLogout}
            className="px-4 py-2 border border-red-300 text-red-600 rounded-lg font-medium hover:bg-red-50"
          >
            Sign Out
          </button>
        </div>
        <div className="space-y-3 text-sm text-gray-600">
          {user.joinDate && <div>Account created: {new Date(user.joinDate).toLocaleDateString()}</div>}
          <div>User ID: {user.id}</div>
        </div>
      </div>
    </div>
  );
};

export default ProfileView;
